import React from 'react';
import { Check } from 'lucide-react';
import { getPageVisual, renderPageIcon } from '../../pageVisuals';

interface TakeoffPageSelectionModalProps {
  show: boolean;
  title: string;
  numPages: number | null;
  pageIndex: Array<{ page: number; label?: string; sheetName?: string }> | null | undefined;
  selectedPages: Set<number>;
  powerDevices?: Array<{ name: string; referenceImage: string }>;
  onTogglePage: (page: number) => void;
  onCancel: () => void;
  onBack?: () => void;
  onConfirm: () => void;
  confirmLabel: string;
}

export const TakeoffPageSelectionModal: React.FC<TakeoffPageSelectionModalProps> = ({
  show,
  title,
  numPages,
  pageIndex,
  selectedPages,
  powerDevices = [],
  onTogglePage,
  onCancel,
  onBack,
  onConfirm,
  confirmLabel,
}) => {
  if (!show) return null;

  type BadgeColors = { bg: string; color: string; border: string };
  const getBadgeColors = (label?: string): BadgeColors | null => {
    const labelLower = label?.toLowerCase() || '';
    if (labelLower.includes('power')) return { bg: '#dcfce7', color: 'var(--green-text)', border: '#bbf7d0' };
    if (labelLower.includes('lighting') || labelLower.includes('reflected ceiling plan') || labelLower.includes('rcp')) {
      return { bg: '#fef9c3', color: 'var(--yellow-text)', border: '#fde68a' };
    }
    if (labelLower.includes('demolition') || labelLower.includes('demo')) return { bg: 'var(--red-tint-medium)', color: '#dc2626', border: '#fecaca' };
    if (labelLower.includes('fire alarm')) return { bg: '#ffedd5', color: 'var(--yellow-text-dark)', border: '#fed7aa' };
    if (labelLower.includes('low voltage') || labelLower.includes('telecom')) return { bg: '#ede9fe', color: 'var(--purple-text)', border: '#ddd6fe' };
    if (labelLower.includes('panel')) return { bg: '#cffafe', color: 'var(--cyan-text-dark)', border: '#a5f3fc' };
    if (label) return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'var(--border-color)' };
    return null;
  };

  const pageInfoByPage = new Map<number, { page: number; label?: string; sheetName?: string }>();
  (pageIndex || []).forEach((entry) => pageInfoByPage.set(entry.page, entry));

  const allPages = numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : [];
  const selectedPageList = allPages.filter((pg) => selectedPages.has(pg));
  const unselectedPageList = allPages.filter((pg) => !selectedPages.has(pg));
  const lightingControlDevices = powerDevices.filter(
    (device) => device.name === 'Switches' || device.name === 'Junction Boxes'
  );

  const renderDeviceChip = (
    device: { name: string; referenceImage: string },
    tone: 'power' | 'controls' = 'power'
  ) => {
    const isControls = tone === 'controls';
    const chipTextColor = isControls ? 'var(--yellow-text)' : 'var(--green-text)';
    const chipBgColor = isControls ? '#fef3c7' : '#dcfce7';
    const chipBorderColor = isControls ? '#fde68a' : '#bbf7d0';
    const placeholderBorder = isControls ? 'rgba(180, 83, 9, 0.35)' : 'rgba(16, 185, 129, 0.35)';
    const placeholderBg = isControls ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)';

    return (
      <span
        key={`${device.name}-${device.referenceImage}-${tone}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '11px',
          fontWeight: 600,
          color: chipTextColor,
          background: chipBgColor,
          border: `1px solid ${chipBorderColor}`,
          borderRadius: '999px',
          padding: '2px 8px',
        }}
      >
        {device.referenceImage ? (
          <img
            src={device.referenceImage}
            alt={device.name}
            style={{
              width: '14px',
              height: '14px',
              objectFit: 'cover',
              borderRadius: '3px',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              background: '#fff',
            }}
          />
        ) : (
          <span
            style={{
              width: '14px',
              height: '14px',
              borderRadius: '3px',
              border: `1px solid ${placeholderBorder}`,
              background: placeholderBg,
            }}
          />
        )}
        <span>{device.name}</span>
      </span>
    );
  };

  const renderPageCard = (pg: number) => {
    const selected = selectedPages.has(pg);
    const info = pageInfoByPage.get(pg);
    const badgeColors = getBadgeColors(info?.label);
    const visual = getPageVisual(info?.label);

    return (
      <button
        key={pg}
        type="button"
        onClick={() => onTogglePage(pg)}
        style={{
          padding: 0,
          border: selected ? '3px solid #3b82f6' : '2px solid var(--border-color)',
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'var(--bg-secondary)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            padding: '8px',
            background: 'var(--bg-secondary)',
          }}
        >
          <div
            style={{
              width: '100%',
              aspectRatio: '1.6 / 1',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-secondary)',
            }}
          >
            {renderPageIcon(visual.type, visual.iconColor)}
          </div>
        </div>
        <span
          style={{
            padding: '6px',
            fontSize: '12px',
            fontWeight: 600,
            color: selected ? '#2563eb' : 'var(--text-secondary)',
            textAlign: 'center',
            background: 'var(--bg-primary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <span>
            Page {pg}
            {selected && <Check size={14} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />}
          </span>
          {badgeColors && info?.label && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: badgeColors.color,
                background: badgeColors.bg,
                border: `1px solid ${badgeColors.border}`,
                borderRadius: '6px',
                padding: '1px 6px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {info.sheetName ? `${info.sheetName} · ` : ''}{info.label}
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100015,
        padding: '20px',
        backdropFilter: 'blur(4px)',
      }}
      onClick={() => {}}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '24px',
          width: '95vw',
          maxWidth: '1280px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid var(--border-color)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, marginBottom: '4px', color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600 }}>
          {title}
        </h3>
        {powerDevices.length > 0 && (
          <div
            style={{
              marginBottom: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', flex: '1 1 420px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
                Power devices:
              </span>
              {powerDevices.map((device) => renderDeviceChip(device, 'power'))}
            </div>
            {lightingControlDevices.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '6px',
                  marginLeft: 'auto',
                  flex: '1 1 320px',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  Lighting controls:
                </span>
                {lightingControlDevices.map((device) => renderDeviceChip(device, 'controls'))}
              </div>
            )}
          </div>
        )}
        <div style={{ flex: 1, marginBottom: '20px', minHeight: '260px', display: 'grid', gridTemplateRows: '3fr 1.45fr', gap: '12px' }}>
          <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '10px' }}>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
              Default Selected Pages ({selectedPageList.length})
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {selectedPageList.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                  {selectedPageList.map(renderPageCard)}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--text-placeholder)', padding: '8px 4px' }}>
                  No pages selected yet.
                </div>
              )}
            </div>
          </div>

          <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '10px' }}>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
              Click on boxes to select other pages ({unselectedPageList.length})
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {unselectedPageList.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                  {unselectedPageList.map(renderPageCard)}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--text-placeholder)', padding: '8px 4px' }}>
                  All pages are selected.
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--text-secondary)',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: 'auto',
              }}
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={selectedPages.size === 0}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              background: selectedPages.size === 0 ? 'var(--text-placeholder)' : '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedPages.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {confirmLabel} ({selectedPages.size} {selectedPages.size === 1 ? 'page' : 'pages'})
          </button>
        </div>
      </div>
    </div>
  );
};
